/**
 * @boosting/ai - Gemini Service (Appmor Implementation)
 *
 * AI Gateway con anonimización transparente (GDPR Art. 4(5)).
 * Todos los servicios deben usar generateContent/executeTask para acceder a IA.
 * La anonimización se maneja aquí centralmente — los consumidores NO deben
 * importar ni usar aiAnonymizationService directamente.
 */

import { functions } from '@/core/config/firebase';
import { createGeminiService, GeminiService, handleGeminiError } from '@boosting/ai';
import { Logger } from '@/core/utils';
import type { SuggestedPractice } from '@/core/types/app-state';
import { aiAnonymizationService, AnonymizationMap } from '@/core/security/ai-anonymization.service';
import { sanitizePromptInput } from '@/core/security/prompt-sanitization';

// ============================================
// INITIALIZATION
// ============================================

// Call to Cloud Function (acts as proxy)
const callGeminiCloudFunction = functions.httpsCallable('callGemini', {
    timeout: 300000 // 5 minutes
});

// Initialize shared service
const geminiService = createGeminiService(callGeminiCloudFunction);
const logger = Logger.getLogger('GeminiService');

// ============================================
// ANONYMIZATION OPTIONS
// ============================================

export interface AnonymizationOptions {
  /** Whether to anonymize (default: true) */
  enabled?: boolean;
  /** Known names to replace (e.g. patient name, family members) */
  knownNames?: string[];
  /** Patient name (gets replaced with PACIENTE) */
  patientName?: string;
  /** For executeTask: which data fields contain text to anonymize */
  fields?: string[];
  /** Which response fields to deanonymize (for object responses) */
  deanonymizeFields?: string[];
}

/**
 * Recursively deanonymizes specified fields in an object response.
 */
function deanonymizeResponseFields<T>(response: T, fields: string[], map: AnonymizationMap): T {
  if (!response || typeof response !== 'object') return response;
  const result = { ...response } as any;
  for (const field of fields) {
    if (field in result && typeof result[field] === 'string') {
      result[field] = aiAnonymizationService.deanonymizeResponse(result[field], map);
    } else if (field in result && Array.isArray(result[field])) {
      result[field] = result[field].map((item: any) =>
        typeof item === 'string' ? aiAnonymizationService.deanonymizeResponse(item, map) : item
      );
    }
  }
  return result;
}

/**
 * Recursively anonymizes all string values within a complex value (object, array, or string).
 * Used when a field is not a plain string but contains nested strings (e.g., array of objects).
 */
function anonymizeFieldValue(value: unknown, map: AnonymizationMap, knownNames: string[], patientName?: string): unknown {
  if (typeof value === 'string') {
    const { anonymizedText } = aiAnonymizationService.anonymizeText(value, knownNames, patientName, map);
    return anonymizedText;
  }
  if (Array.isArray(value)) {
    return value.map(item => anonymizeFieldValue(item, map, knownNames, patientName));
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = anonymizeFieldValue(v, map, knownNames, patientName);
    }
    return result;
  }
  return value;
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Transcribes an audio file by calling the secure backend function.
 */
export const transcribeAudio = async (audioBase64: string, mimeType: string): Promise<string> => {
    return geminiService.transcribeAudio(audioBase64, mimeType);
};

/**
 * Generates practices from a document by calling the secure backend function.
 */
export const generatePracticesFromDocument = async (
    documentParts: { inlineData: { data: string; mimeType: string; } }[],
    categoryId?: string
): Promise<SuggestedPractice[] | string> => {
    // Cast to expected type or handle mapping if necessary
    return geminiService.generatePracticesFromDocument<SuggestedPractice>(documentParts, categoryId);
};

/**
 * Generates content using a specific task and prompt.
 * Automatically anonymizes the prompt and deanonymizes the response (GDPR Art. 4(5)).
 */
export const generateContent = async <T = string>(
    prompt: string,
    options?: {
      task?: string;
      model?: any;
      temperature?: number;
      maxTokens?: number;
      anonymize?: AnonymizationOptions;
    }
): Promise<T> => {
    const anonOpts = options?.anonymize;
    const shouldAnonymize = anonOpts?.enabled !== false;

    if (!shouldAnonymize) {
      const result = await geminiService.generateContent<T>(prompt, options as any);
      return result.data;
    }

    // Anonymize prompt
    const { anonymizedText, map } = aiAnonymizationService.anonymizeText(
      prompt,
      anonOpts?.knownNames || [],
      anonOpts?.patientName
    );

    const result = await geminiService.generateContent<T>(anonymizedText, options as any);
    const data = result.data;

    // Deanonymize response
    if (typeof data === 'string') {
      return aiAnonymizationService.deanonymizeResponse(data, map) as unknown as T;
    }
    if (anonOpts?.deanonymizeFields?.length && typeof data === 'object' && data !== null) {
      return deanonymizeResponseFields(data, anonOpts.deanonymizeFields, map);
    }
    return data;
};

/**
 * Executes a generic task with arbitrary data.
 * Automatically anonymizes specified data fields and deanonymizes response fields (GDPR Art. 4(5)).
 */
export const executeTask = async <T = unknown>(
    task: string,
    data: Record<string, unknown>,
    options?: { anonymize?: AnonymizationOptions }
): Promise<T> => {
    const anonOpts = options?.anonymize;
    const shouldAnonymize = anonOpts?.enabled !== false && anonOpts?.fields?.length;

    if (!shouldAnonymize) {
      return geminiService.executeTask<T>(task, data);
    }

    // Anonymize specified fields in data (shared map ensures consistent pseudonyms)
    const anonymizedData = { ...data };
    const map = aiAnonymizationService.createMap(anonOpts.patientName);

    for (const field of anonOpts.fields!) {
      if (field in anonymizedData) {
        anonymizedData[field] = anonymizeFieldValue(
          anonymizedData[field],
          map,
          anonOpts.knownNames || [],
          anonOpts.patientName
        );
      }
    }

    const result = await geminiService.executeTask<T>(task, anonymizedData);

    // Deanonymize response fields
    if (anonOpts.deanonymizeFields?.length && typeof result === 'object' && result !== null) {
      return deanonymizeResponseFields(result, anonOpts.deanonymizeFields, map);
    }
    return result;
};

// DEPRECATED: generateWeeklyPlan, getCounselorAdvice, generateDailyImprovementSuggestion removidos

export interface GeneratedGenogramMember {
    tempId: string;
    firstName: string;
    lastName?: string;
    gender: 'male' | 'female' | 'other';
    age?: number;
    birthYear?: number;
    vitalStatus: 'alive' | 'deceased' | 'unknown';
    isPatient?: boolean;
    occupation?: string;
    healthIssues?: string[];
    notes?: string;
    generation: 'extended' | 'origin' | 'created' | 'descendant';
    generationLevel: number;
    familyRole?: string;
}

export interface GeneratedGenogramRelationship {
    member1TempId: string;
    member2TempId: string;
    relationType: 'close' | 'very_close' | 'distant' | 'hostile' | 'conflictual' | 'cut_off' | 'normal' | 'unknown';
    isParentChild?: boolean;
    maritalStatus?: 'married' | 'cohabiting' | 'separated' | 'divorced' | 'widowed' | 'dating';
    description?: string;
}

export interface GeneratedGenogramMemberUpdate {
    existingId: string; // "existing_0", "existing_1", etc.
    notes?: string;
    healthIssues?: string[];
    vitalStatus?: 'alive' | 'deceased' | 'unknown';
    familyRole?: string;
    occupation?: string;
}

export interface GeneratedGenogram {
    members: GeneratedGenogramMember[];
    relationships: GeneratedGenogramRelationship[];
    memberUpdates?: GeneratedGenogramMemberUpdate[];
    relationshipUpdates?: GeneratedGenogramRelationshipUpdate[];
    patterns?: string[];
    hypotheses?: string[];
    therapeuticNotes?: string;
}

export interface ExistingMemberInfo {
    firstName: string;
    age?: number;
    gender: 'male' | 'female' | 'other';
    isPatient?: boolean;
    id: string;
    parentOf?: string[];   // firstNames of their children
    childOf?: string[];    // firstNames of their parents
    spouseOf?: string[];   // firstNames of their spouses
}

export interface GeneratedGenogramRelationshipUpdate {
    member1ExistingId: string; // "existing_0", "existing_1", etc.
    member2ExistingId: string;
    relationType?: 'close' | 'very_close' | 'distant' | 'hostile' | 'conflictual' | 'cut_off' | 'normal' | 'unknown';
    description?: string;
}

/**
 * Genera un genograma estructurado a partir de una descripción textual de la familia.
 * Aplica anonimización antes de enviar a IA y des-anonimiza la respuesta.
 */
export const generateGenogramFromText = async (
    familyDescription: string,
    patientName?: string,
    existingMembers?: ExistingMemberInfo[]
): Promise<GeneratedGenogram | string> => {
    // Anonymize patient data before sending to external AI
    const memberNames = existingMembers?.map(m => m.firstName) || [];
    const { anonymizedContext, map } = aiAnonymizationService.anonymizePatientContext({
        patientName,
        familyDescription: sanitizePromptInput(familyDescription),
        genogramMembers: existingMembers?.map(m => ({ name: m.firstName, ...m })),
    });

    const result = await geminiService.generateGenogramFromText(
        anonymizedContext.familyDescription || familyDescription,
        anonymizedContext.patientName,
        existingMembers as any
    );

    if (typeof result === 'string') {
        return aiAnonymizationService.deanonymizeResponse(result, map);
    }

    // Deanonymize member names in the result
    const genogram = result as unknown as GeneratedGenogram;
    genogram.members = genogram.members.map(member => ({
        ...member,
        firstName: aiAnonymizationService.deanonymizeResponse(member.firstName, map),
        notes: member.notes ? aiAnonymizationService.deanonymizeResponse(member.notes, map) : undefined,
    }));
    if (genogram.therapeuticNotes) {
        genogram.therapeuticNotes = aiAnonymizationService.deanonymizeResponse(genogram.therapeuticNotes, map);
    }
    if (genogram.patterns) {
        genogram.patterns = genogram.patterns.map(p => aiAnonymizationService.deanonymizeResponse(p, map));
    }
    if (genogram.hypotheses) {
        genogram.hypotheses = genogram.hypotheses.map(h => aiAnonymizationService.deanonymizeResponse(h, map));
    }

    return genogram;
};